/**
 * GlobalSearchProvider — V107.52.34
 *
 * Single source of truth for the global search modal state:
 *   - isOpen, query, activeFilter
 *   - Recent searches (persisted to localStorage per-user)
 *   - Keyboard shortcut wiring (Cmd+K / Ctrl+K / "/" — scoped to office routes)
 *
 * Mounted once inside OfficeLayout (NOT App.tsx) so the keyboard listener
 * doesn't fire on tech routes. The modal itself is rendered as a child of
 * this provider (no portal — the modal uses `fixed inset-0` z-[150] which
 * already escapes any layout stacking context within OfficeLayout).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useGlobalSearchKeyboard } from '@/hooks/useGlobalSearchKeyboard';
import type { SearchFilter } from '@/hooks/useGlobalSearch';
import { GlobalSearchModal } from './GlobalSearchModal';

const RECENT_LIMIT = 5;
const STORAGE_KEY_PREFIX = 'globalSearch.recent.';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface GlobalSearchContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  query: string;
  setQuery: (q: string) => void;
  activeFilter: SearchFilter;
  setActiveFilter: (f: SearchFilter) => void;
  recentSearches: string[];
  addRecentSearch: (q: string) => void;
  clearRecentSearches: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

export function useGlobalSearchContext(): GlobalSearchContextValue {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error('useGlobalSearchContext must be used inside <GlobalSearchProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

function storageKey(): string {
  const userId = localStorage.getItem('user_id') || localStorage.getItem('tech_id') || 'anon';
  return STORAGE_KEY_PREFIX + userId;
}

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

function saveRecent(values: string[]): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(values.slice(0, RECENT_LIMIT)));
  } catch {
    /* ignore quota errors */
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ProviderProps {
  children: ReactNode;
}

export function GlobalSearchProvider({ children }: ProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<SearchFilter>('all');
  const [recentSearches, setRecentSearches] = useState<string[]>(() => loadRecent());

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    // Clear query on close so next open is fresh; recent searches persist.
    // (Trade-off: typed query doesn't survive accidental Esc. We preferred
    // the cleaner "fresh start" UX on every open.)
    setQuery('');
    setActiveFilter('all');
  }, []);

  const addRecentSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed || trimmed.length < 2) return;
    setRecentSearches((prev) => {
      // Move-to-front semantics — drop duplicates, then prepend.
      const next = [trimmed, ...prev.filter((x) => x.toLowerCase() !== trimmed.toLowerCase())].slice(
        0,
        RECENT_LIMIT,
      );
      saveRecent(next);
      return next;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    saveRecent([]);
  }, []);

  // Wire keyboard shortcuts (Cmd+K / Ctrl+K / "/" / Esc)
  useGlobalSearchKeyboard({ isOpen, onOpen: open, onClose: close });

  // Lock body scroll while modal is open (prevents background page from
  // jumping under the modal on iOS).
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  const value = useMemo<GlobalSearchContextValue>(
    () => ({
      isOpen,
      open,
      close,
      query,
      setQuery,
      activeFilter,
      setActiveFilter,
      recentSearches,
      addRecentSearch,
      clearRecentSearches,
    }),
    [isOpen, open, close, query, activeFilter, recentSearches, addRecentSearch, clearRecentSearches],
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      {isOpen && <GlobalSearchModal />}
    </GlobalSearchContext.Provider>
  );
}
